import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Upload, FileText, X } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/blocks";
import {
  Field,
  TextField,
  TextAreaField,
  SelectField,
  PillGroup,
  CheckAck,
} from "@/components/FormKit";
import {
  EMIRATES,
  YEARS_EXPERIENCE,
  CHILD_AGE_RANGES,
  NANNY_CARE_TYPES,
  LANGUAGES,
} from "@/lib/options";

const initialData = {
  company: "",
  full_name: "",
  email: "",
  phone: "",
  emirate: "",
  years_experience: "",
  age_groups: [],
  care_types: [],
  languages: [],
  training_qualifications: "",
  first_aid_cpr: false,
  first_aid_expiry: "",
  references_available: false,
  schedule: "",
  intro: "",
  truthfulness_ack: false,
  privacy_ack: false,
};

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const ALLOWED = ["pdf", "doc", "docx", "jpg", "jpeg", "png", "webp"];
const MAX_BYTES = 8 * 1024 * 1024;

export default function ForNannies() {
  useSeo({
    title: "For Nannies — Apply | Nanny Poppins UAE",
    description:
      "Experienced nannies can apply to be represented by Nanny Poppins. Your application and documents stay private to our team.",
  });

  const [data, setData] = useState(initialData);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggle = (key, v) =>
    setData((d) => ({
      ...d,
      [key]: d[key].includes(v) ? d[key].filter((x) => x !== v) : [...d[key], v],
    }));

  const onFile = (e) => {
    setFileError("");
    const f = e.target.files?.[0];
    if (!f) return setFile(null);
    const ext = f.name.split(".").pop().toLowerCase();
    if (!ALLOWED.includes(ext)) {
      setFileError("Please upload a PDF, DOC, DOCX or image file.");
      return;
    }
    if (f.size > MAX_BYTES) {
      setFileError("File is too large (max 8MB).");
      return;
    }
    setFile(f);
  };

  const validate = () => {
    const e = {};
    if (!data.full_name.trim()) e.full_name = "Please enter your name.";
    if (!emailOk(data.email)) e.email = "Please enter a valid email address.";
    if (!data.phone.trim()) e.phone = "Please enter a phone number.";
    if (!data.emirate) e.emirate = "Please select an Emirate.";
    if (!data.years_experience) e.years_experience = "Please select your experience.";
    if (data.age_groups.length === 0) e.age_groups = "Please choose at least one age group.";
    if (data.care_types.length === 0) e.care_types = "Please choose at least one care type.";
    if (data.languages.length === 0) e.languages = "Please choose at least one language.";
    if (!data.truthfulness_ack) e.truthfulness_ack = "Please confirm your information is truthful.";
    if (!data.privacy_ack) e.privacy_ack = "Please acknowledge the privacy notice.";
    setErrors(e);
    if (Object.keys(e).length) {
      const first = document.querySelector(`[data-testid="field-${Object.keys(e)[0]}"]`);
      first?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setStatus("submitting");
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify(data));
      if (file) fd.append("cv", file);
      const res = await api.post("/nanny-applications", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.status === "received") {
        setStatus("success");
        window.scrollTo(0, 0);
      } else {
        setStatus("error");
      }
    } catch (err) {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div>
        <PageHeader eyebrow="For Nannies" title="Thank you for applying." />
        <section className="container-page max-w-2xl py-16" data-testid="nanny-success">
          <div className="rounded-2xl border border-[#C6A04A]/40 bg-white p-10">
            <CheckCircle2 className="h-10 w-10 text-[#C6A04A]" />
            <h2 className="mt-5 font-serif text-2xl tracking-tight">Application received</h2>
            <p className="mt-3 text-[#0B0A08]/70">
              Your application has been saved privately for our team to review. If we can represent you, we'll
              be in touch. Thank you for your interest in Nanny Poppins.
            </p>
            <Link
              to="/"
              data-testid="nanny-success-home"
              className="mt-8 inline-flex items-center justify-center rounded-full bg-[#0B0A08] px-7 py-3.5 text-sm font-semibold text-[#F7F4ED] hover:bg-[#0B0A08]/85"
            >
              Back to home
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="For Nannies"
        title="Apply to be represented."
        intro="If you're an experienced nanny, we'd love to hear from you. Your application and any documents you share stay private to our team — never listed publicly."
      />

      <section className="container-page max-w-2xl py-14">
        {status === "error" && (
          <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-700" data-testid="nanny-error">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <p>We couldn't save your application just now. Nothing was submitted — please try again in a moment.</p>
          </div>
        )}

        {/* Honeypot */}
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={data.company}
          onChange={(e) => set("company", e.target.value)}
          className="absolute left-[-9999px] h-0 w-0 opacity-0"
          data-testid="honeypot-company"
        />

        <div className="space-y-6 rounded-2xl border border-[#0B0A08]/10 bg-white p-6 sm:p-9">
          <Field label="Full name" required error={errors.full_name} htmlFor="full_name">
            <TextField id="full_name" value={data.full_name} onChange={(e) => set("full_name", e.target.value)} error={errors.full_name} placeholder="Your full name" />
          </Field>
          <div className="grid gap-6 sm:grid-cols-2">
            <Field label="Email" required error={errors.email} htmlFor="email">
              <TextField id="email" type="email" value={data.email} onChange={(e) => set("email", e.target.value)} error={errors.email} placeholder="you@example.com" />
            </Field>
            <Field label="Phone" required error={errors.phone} htmlFor="phone">
              <TextField id="phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} error={errors.phone} placeholder="+971 5X XXX XXXX" />
            </Field>
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            <Field label="Emirate" required error={errors.emirate} htmlFor="emirate">
              <SelectField id="emirate" value={data.emirate} onChange={(v) => set("emirate", v)} placeholder="Select Emirate" options={EMIRATES} />
            </Field>
            <Field label="Years of experience" required error={errors.years_experience} htmlFor="years_experience">
              <SelectField id="years_experience" value={data.years_experience} onChange={(v) => set("years_experience", v)} placeholder="Select experience" options={YEARS_EXPERIENCE} />
            </Field>
          </div>

          <Field label="Age groups you've cared for" required error={errors.age_groups} htmlFor="age_groups" hint="Select all that apply.">
            <PillGroup id="age_groups" options={CHILD_AGE_RANGES} selected={data.age_groups} onToggle={(v) => toggle("age_groups", v)} />
          </Field>
          <Field label="Types of care you offer" required error={errors.care_types} htmlFor="care_types" hint="Select all that apply.">
            <PillGroup id="care_types" options={NANNY_CARE_TYPES} selected={data.care_types} onToggle={(v) => toggle("care_types", v)} />
          </Field>
          <Field label="Languages" required error={errors.languages} htmlFor="languages" hint="Select all that apply.">
            <PillGroup id="languages" options={LANGUAGES} selected={data.languages} onToggle={(v) => toggle("languages", v)} />
          </Field>

          <Field label="Training & qualifications" htmlFor="training_qualifications" hint="Optional — courses, certifications or relevant training.">
            <TextAreaField id="training_qualifications" value={data.training_qualifications} onChange={(e) => set("training_qualifications", e.target.value)} placeholder="e.g. Early childhood course, montessori training" />
          </Field>

          <div className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-3">
              <CheckAck id="first_aid_cpr" checked={data.first_aid_cpr} onChange={(v) => set("first_aid_cpr", v)}>
                I hold a current First Aid / CPR certification.
              </CheckAck>
              {data.first_aid_cpr && (
                <Field label="First Aid / CPR expiry" htmlFor="first_aid_expiry" hint="Optional.">
                  <TextField id="first_aid_expiry" value={data.first_aid_expiry} onChange={(e) => set("first_aid_expiry", e.target.value)} placeholder="e.g. March 2027" />
                </Field>
              )}
            </div>
            <CheckAck id="references_available" checked={data.references_available} onChange={(v) => set("references_available", v)}>
              I can provide references on request.
            </CheckAck>
          </div>

          <Field label="Availability / schedule" htmlFor="schedule" hint="Optional.">
            <TextField id="schedule" value={data.schedule} onChange={(e) => set("schedule", e.target.value)} placeholder="e.g. Full-time, live-out, weekdays" />
          </Field>
          <Field label="A short introduction" htmlFor="intro" hint="Optional — tell families a little about yourself.">
            <TextAreaField id="intro" value={data.intro} onChange={(e) => set("intro", e.target.value)} placeholder="Your approach to caring for children, what you enjoy most, etc." />
          </Field>

          {/* CV upload */}
          <Field label="CV or certificate (optional)" htmlFor="cv" hint="PDF, DOC, DOCX or image — max 8MB. Kept private to our team.">
            {file ? (
              <div className="flex items-center justify-between rounded-xl border border-[#0B0A08]/15 bg-[#F7F4ED] px-4 py-3" data-testid="cv-selected">
                <span className="flex items-center gap-2 text-sm text-[#0B0A08]/80">
                  <FileText className="h-4 w-4 text-[#C6A04A]" /> {file.name}
                </span>
                <button type="button" onClick={() => setFile(null)} data-testid="cv-remove" aria-label="Remove file" className="text-[#0B0A08]/50 hover:text-red-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label
                htmlFor="cv"
                className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-[#0B0A08]/25 bg-[#F7F4ED] px-4 py-6 text-sm text-[#0B0A08]/60 hover:border-[#C6A04A]"
              >
                <Upload className="h-5 w-5 text-[#C6A04A]" /> Choose a file to upload
                <input id="cv" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" onChange={onFile} className="hidden" data-testid="field-cv" />
              </label>
            )}
            {fileError && <p className="mt-1 text-xs font-medium text-red-600" data-testid="cv-error">{fileError}</p>}
          </Field>

          <p className="text-xs text-[#0B0A08]/50">
            Please don't upload passports, Emirates IDs, visa or bank details. Share only what's relevant to
            your childcare experience.
          </p>

          <div className="space-y-4 border-t border-[#0B0A08]/10 pt-6">
            <CheckAck id="truthfulness_ack" checked={data.truthfulness_ack} onChange={(v) => set("truthfulness_ack", v)} error={errors.truthfulness_ack}>
              I confirm the information I've provided is truthful and accurate. *
            </CheckAck>
            <CheckAck id="privacy_ack" checked={data.privacy_ack} onChange={(v) => set("privacy_ack", v)} error={errors.privacy_ack}>
              I've read and understand the{" "}
              <Link to="/privacy-policy" className="font-medium text-[#8a6d1f] underline">Privacy Policy</Link>{" "}
              and consent to my details being reviewed by the team. *
            </CheckAck>
          </div>

          <button
            type="button"
            onClick={submit}
            disabled={status === "submitting"}
            data-testid="nanny-submit"
            className="inline-flex w-full items-center justify-center rounded-full bg-[#C6A04A] px-6 py-3.5 text-sm font-semibold text-[#0B0A08] hover:scale-[1.01] disabled:opacity-60 sm:w-auto"
          >
            {status === "submitting" ? "Submitting…" : "Submit application"}
          </button>
        </div>
      </section>
    </div>
  );
}
